import {
  CHROME_EYE_GAP_EXTRA,
  CHROME_GAP,
  CHROME_ICON_SEAT,
  CHROME_LABEL_FLOOR,
  CHROME_T1,
  CHROME_T2,
} from "./chromeConstants";
import { CHROME_FOOT_FONT, measureChromeText } from "./textMeasure";

/**
 * The three-regime fit — ported from the design system's regime lab
 * (staging/object-matrix-v2, rulings of 2026-08-12).
 *
 * Regime 1 (zoom ≥ T1, active): full chrome, screen-constant. The label
 * truncates first — verbs never drop (rest/hover value inversion: at rest
 * the label owns the full width; the only moment label and verbs compete
 * is on hover, exactly when the actions matter more).
 * Regime 2 (T2 ≤ zoom < T1, or inactive): label alone, screen-constant,
 * spanning the node's full top width.
 * Regime 3 (zoom < T2): the label recedes with the canvas — the
 * counter-scale clamps at 1/T2 and the layout freezes at the T2 state.
 */

export interface ChromeFitInput {
  /** Node width in flow (world) px. */
  width: number;
  /** Current canvas zoom. */
  zoom: number;
  /** Hovered or pinned — reveals verbs, eye, and foot (regime 1 only). */
  active: boolean;
  /** Right-anchored verbs this node carries (note: quick-chat + open-in-pane = 2). */
  verbCount: number;
  /** Foot ladder, longest first, "" as the always-fitting last rung. */
  footRungs: readonly string[];
  /** Space the foot's fixed elements claim beside the text (chip, download seat, gaps). */
  footFixed: number;
}

export interface ChromeFit {
  /** Verbs + eye + foot present (regime 1 and active). */
  showVerbs: boolean;
  /** Band width in band-local px (node width × max(zoom, T2)). */
  bandWidth: number;
  /** Max label width, band-local px. */
  labelMax: number;
  /** The foot rung that fits, or "" when none does. */
  foot: string;
  /** Counter-scale for the chrome layer: 1 / max(zoom, T2). */
  scale: number;
}

export function chromeFit({
  width,
  zoom,
  active,
  verbCount,
  footRungs,
  footFixed,
}: ChromeFitInput): ChromeFit {
  const m = Math.max(zoom, CHROME_T2);
  const bandWidth = width * m;
  const showVerbs = zoom >= CHROME_T1 && active;

  let labelMax: number;
  if (!showVerbs) {
    labelMax = bandWidth;
  } else {
    const reserved =
      (CHROME_ICON_SEAT + CHROME_GAP) * (1 + verbCount) + CHROME_EYE_GAP_EXTRA;
    labelMax = Math.max(bandWidth - reserved, CHROME_LABEL_FLOOR);
  }

  const footAvail = bandWidth - footFixed;
  const foot =
    footRungs.find(
      (rung) =>
        rung === "" || measureChromeText(rung, CHROME_FOOT_FONT) <= footAvail,
    ) ?? "";

  return { showVerbs, bandWidth, labelMax, foot, scale: 1 / m };
}

/**
 * Foot ladder from authorship — longest first, degrading by dropping
 * precision before dropping identity: "Ada Lovelace · 2h ago" →
 * "Ada · 2h" → "Ada" → "". Without a timestamp (legacy notes) the name
 * still renders alone.
 */
export function buildFootRungs(
  name: string,
  when?: string,
  shortWhen?: string,
): string[] {
  const first = name.split(/\s+/)[0] ?? name;
  if (!when) return [...new Set([name, first, ""])];
  const compact = shortWhen ?? when;
  const rungs = [`${name} · ${when}`, `${first} · ${compact}`, first, ""];
  return [...new Set(rungs)];
}
